import { Component, OnInit, ViewEncapsulation, Inject, ViewChild, ElementRef } from '@angular/core';
import { StateStructure } from '@ptl/state';
import { NgRedux } from '@ptl/angular-redux-store';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { County } from 'src/app/angular-elements/shared/interfaces/zip-county-response.interface';
import { UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl, UntypedFormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { setRecommendPlansFlowUsageCode } from '../../plan-selection/configs/reducers/plans.reducer';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { DateService } from 'src/app/shared/services/date.service';
import { ShoppingDateStateStructure } from '../../shared/components/configs/interfaces/shopping-date.state-structure.interface';
import { PtlWindowLocationService } from '@ptl/global-components';
import { SHOPPING_DATE_STATE_NAME } from '../../shared/components/configs/reducers/shopping-dates.reducer';
import { PassiveReferralLoaderService } from '../../producer/passive-referral-loader/services/passive-referral-loader.service';
import { setPassiveReferralEmail} from '../../producer/passive-referral-loader/reducers/passive-referral-loader.reducer';
import { ProducerUrlSessionObject } from '../../producer/passive-referral-loader/interfaces/passive-referral-loader.interface';
import { EffectiveDateStateStructure } from '../../shared/components/configs/interfaces/effective-date.state-structure.interface';
import { EFFECTIVE_DATE_STATE_NAME } from '../../shared/components/configs/reducers/effective-date.reducer';
import { GlobalShadowStyleDirective } from '@ptl/global-components';

@Component({
    selector: 'app-zip-county-modal',
    templateUrl: './zip-county-modal.component.html',
    styleUrls: ['./zip-county-modal.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    standalone: false
})
export class ZipCountyModalComponent extends GlobalShadowStyleDirective implements OnInit {

  form: UntypedFormGroup;
  showYearSelector: boolean = false;
  includeEmailField: boolean = false;
  disableClose: boolean = false;

  /** Stores the selected county */
  saveableCounty: County;
  saveableZipCode: string;
  saveableEmail: string; 
  formValid: boolean;
  passiveReferralData: ProducerUrlSessionObject;
  nextMonthsYearIsThisYear: boolean = true;
  goToUrlCalled: boolean = false;

  defaultHeaderText: string;
  defaultBasicBodyText: string;
  defaultYearSelectorBodyText: string;
  viewPlansButtonLabel: string;
  viewPlansConnectureLabel: string;

  displayContent = {
    headerText: '',
    basicBodyText: '',
    yearSelectorBodyText: ''
  };

  stateValues: { zipCode: string, emailAddress: string } = {zipCode: "",  emailAddress: ""}
  @ViewChild('zipInputs', { static: true }) zipInputs: ElementRef;

  constructor(
    private date: DateService,
    private ngRedux: NgRedux<StateStructure>,
    private reducerUtilityService: ReducerUtilityService,
    private dialogRef: MatDialogRef<ZipCountyModalComponent>,
    private commonUtilityService: CommonUtilityService,
    private windowLocationService: PtlWindowLocationService,
    private passiveReferralService: PassiveReferralLoaderService,
    @Inject(MAT_DIALOG_DATA) public data: {
      forceBranding: boolean,
      viewPlansUrl: string,
      connectureUrl: string,
      outboundUrl: string,
      recommendedPlansUrl: string,
      redirectClosedUrl: string,
      includeEmailField: boolean,
      hideViewPlansButton: boolean,
      showConnectureButton: boolean,
      isConnectureNewTab: boolean,
      yearSelectionConfig: {
        currentYearOverride: string,
        hideYearSelection: boolean,
        selectYearHeading: string,
        selectYearPrompt: string,
        initialYearSelection: string,
        tooltipText: string
      },
      modalContent: {
        heading: string,
        contentMain: string,
        buttonLabel: string,
        connectureButtonLabel: string
      }
    } | any
  ) {
    super()
  }

  ngOnInit() {
    // Initalize zip and county if we have one
    const zipcountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    this.passiveReferralData = this.reducerUtilityService.getState('PassiveReferrals')
    if (zipcountyState.zipCode) {
      this.stateValues.zipCode = zipcountyState.zipCode
    }
    if (this.passiveReferralData.emailAddress) {
      this.stateValues.emailAddress = this.passiveReferralData.emailAddress;
    }

    // Mat dialog has a bug that doesn't let us choose the active focus element on load (always defaults to focus the dialog root)
    // This waits until the mat dialog tab logic runs so it doesn't steal the focus
    setTimeout(() => {
      this.zipInputs.nativeElement.getElementsByTagName('input')[0].focus();
    }, 500);

    // Calculate booleans
    this.disableClose = this.dialogRef.disableClose;
    const shoppingDates: ShoppingDateStateStructure = this.reducerUtilityService.getApplicationStates()[SHOPPING_DATE_STATE_NAME];
    this.showYearSelector = !this.data?.yearSelectionConfig?.hideYearSelection && this.commonUtilityService.shouldShowYearSelectorByDates(shoppingDates);
    this.includeEmailField = this.data?.includeEmailField ? true : false;

    // Need to override current year if we have a special december config
    if (shoppingDates.sellThisYearProductsDate && this.date.getTodayAsMoment().isSameOrBefore(shoppingDates.sellThisYearProductsDate)) {
      this.data.yearSelectionConfig.currentYearOverride = this.date.getTodayAsMoment().year().toString();
    }

    // Setup default string values
    const zipLabel = $localize`Please enter your zip code`;
    this.defaultHeaderText = $localize`Welcome to Highmark!`;
    this.defaultBasicBodyText = this.includeEmailField ? $localize`${zipLabel}, then enter your email address.` : zipLabel;
    this.defaultYearSelectorBodyText = $localize`Please select your enrollment year, then enter your zip code.`;
    
    this.displayContent.headerText = this.data?.modalContent?.heading || this.defaultHeaderText;
    this.displayContent.basicBodyText = this.data?.modalContent?.contentMain || this.defaultBasicBodyText;
    this.displayContent.yearSelectorBodyText = this.data?.modalContent?.contentMain || this.defaultYearSelectorBodyText;
    this.viewPlansButtonLabel = this.data?.modalContent?.buttonLabel || $localize`View Plans`;
    this.viewPlansConnectureLabel = this.data?.modalContent?.connectureButtonLabel || $localize`Connecture`;
  }

  zipCountyChanged(value: { county: County; zipCode: string; }) {
    this.saveableCounty = value.county;
    this.saveableZipCode = value.zipCode;
  }

  emailChanged(value: { email: string, formValid: boolean}){
    if(value.formValid && value.email){
      this.saveableEmail = value.email;
    }else {
      this.saveableEmail = "" 
    }
  }

  closeModal(isCancelled: boolean) {
    const zipcountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');

    if (this.data.forceBranding && (!zipcountyState.zipCode || !zipcountyState.county?.county)) {
      this.windowLocationService.goToUrl(this.data?.redirectClosedUrl || '/home');
    }
    else {
      this.dialogRef.close(isCancelled);
    }
  }

  async gotoURL(url: string) {
    const ifEmailExists = (this.saveableEmail || this.passiveReferralData.emailAddress) ? true : false; 
    if (this.saveableCounty && this.saveableZipCode && ( !this.includeEmailField || (this.includeEmailField && ifEmailExists ))) {
      /** Wipe the plan usage level value */
      // const passiveReferralData: ProducerUrlSessionObject = this.reducerUtilityService.getState('PassiveReferrals')
      if(this.includeEmailField && this.saveableEmail && !this.goToUrlCalled){
          this.goToUrlCalled = true;
          this.ngRedux.dispatch(setPassiveReferralEmail(this.saveableEmail))
          await this.passiveReferralService.assignPassiveReferralInfo(this.saveableEmail)    
      }

      this.commonUtilityService.saveCountyZipData(this.saveableCounty, this.saveableZipCode);
      this.ngRedux.dispatch(setRecommendPlansFlowUsageCode(null))


      if (this.data?.outboundUrl) {
        //this.brandAware.goToBrandedUrl(this.data.outboundUrl);
        this.dialogRef.close();
      }
      else if (url) {
        setTimeout(()=>{this.windowLocationService.goToUrl(url);},500);
      }
      else {
        setTimeout(()=>{window.location.reload()},500);
      }
    }
  }

  goToConnecture() {
    if (!this.saveableZipCode || !this.saveableCounty?.fipsCode) {
      return;
    }

    let effectiveDateState:EffectiveDateStateStructure = this.reducerUtilityService.getApplicationStates()[EFFECTIVE_DATE_STATE_NAME];

    let url = this.data.connectureUrl;
    url = url.replaceAll('<<planYear>>', effectiveDateState?.effectiveDateYear);
    url += `?ZipCode=${this.saveableZipCode}&CountyFIPS=${this.saveableCounty.fipsCode}`;
    this.windowLocationService.goToUrl(url, {newTab: this.data.isConnectureNewTab});

    if (this.data.isConnectureNewTab) {
      window.location.reload();
    }
  }
}
